import { logger } from "@utilities/logger";
import { useEffect } from "react";

/** Pass stable `actions` (e.g. `useCallback` per method) so handlers are not re-registered every render. */
export function useMediaSession(options: {
  title: string | null;
  author: string | null;
  loaded: boolean;
  actions: {
    play: () => void;
    pause: () => void;
    seekTo: (seekTime: number) => void;
    seekBySeconds: (amount: number) => void;
  };
}) {
  const { play, pause, seekTo, seekBySeconds } = options.actions;

  useEffect(() => {
    if (!options.loaded) {
      return;
    }
    if ("mediaSession" in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: options.title ?? "",
        artist: options.author ?? "",
      });
      const actionHandlers: Partial<Record<MediaSessionAction, MediaSessionActionHandler | null>> = {
        play: () => play(),
        pause: () => pause(),
        previoustrack: () => seekTo(0),
        nexttrack: null,
        stop: () => {
          pause();
          seekTo(0);
        },
        seekto: (details: MediaSessionActionDetails) => {
          if (details.seekTime != null) {
            seekTo(details.seekTime);
          }
        },
        seekbackward: (details: MediaSessionActionDetails) => {
          if (details.seekOffset != null) {
            seekBySeconds(-details.seekOffset);
          }
        },
        seekforward: (details: MediaSessionActionDetails) => {
          if (details.seekOffset != null) {
            seekBySeconds(details.seekOffset);
          }
        },
      };
      for (const action of Object.keys(actionHandlers) as MediaSessionAction[]) {
        try {
          navigator.mediaSession.setActionHandler(action, actionHandlers[action] ?? null);
        } catch {
          logger.log(`The media session action "${action}" is not supported yet.`);
        }
      }
    }
  }, [options.title, options.author, options.loaded, play, pause, seekTo, seekBySeconds]);
}
